import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { X, Camera } from 'lucide-react';

const BarcodeScanner = ({ onScanSuccess, onClose }) => {
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const isProcessingRef = useRef(false); // Prevent duplicate scans
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initScanner = async () => {
      try {
        console.log("Initializing barcode scanner...");
        
        // Create scanner instance
        const scanner = new Html5Qrcode("barcode-scanner-region");
        html5QrCodeRef.current = scanner;

        const config = {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          formatsToSupport: [
            Html5Qrcode.SCAN_TYPE_CODE_128,
            Html5Qrcode.SCAN_TYPE_EAN_13,
            Html5Qrcode.SCAN_TYPE_EAN_8,
            Html5Qrcode.SCAN_TYPE_UPC_A,
            Html5Qrcode.SCAN_TYPE_UPC_E,
          ]
        };

        const qrCodeSuccessCallback = (decodedText, decodedResult) => {
          // Prevent duplicate processing
          if (isProcessingRef.current) {
            console.log('Already processing, ignoring duplicate scan');
            return;
          }
          
          isProcessingRef.current = true;
          console.log(`Barcode scanned: ${decodedText}`);
          setIsScanning(false);
          
          // Stop scanner immediately to prevent more scans
          if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
            html5QrCodeRef.current.stop()
              .then(() => {
                console.log("Scanner stopped after successful scan");
                onScanSuccess(decodedText);
              })
              .catch(err => {
                console.error("Error stopping scanner:", err);
                onScanSuccess(decodedText);
              });
          } else {
            onScanSuccess(decodedText);
          }
        };

        const qrCodeErrorCallback = (errorMessage) => {
          // Suppress "No MultiFormat Readers were able to detect the code" spam
          if (!errorMessage.includes('NotFoundException') && 
              !errorMessage.includes('No MultiFormat')) {
            console.warn(`Scan error: ${errorMessage}`);
          }
        };

        // Start scanning with rear camera
        await scanner.start(
          { facingMode: "environment" },
          config,
          qrCodeSuccessCallback,
          qrCodeErrorCallback
        );

        console.log("Scanner started successfully");
        setIsScanning(true);
        setError(null);

      } catch (err) {
        console.error("Error initializing scanner:", err);
        setError(`Failed to access camera: ${err.message || 'Please allow camera permissions'}`);
      }
    };

    initScanner();

    // Cleanup function
    return () => {
      if (html5QrCodeRef.current) {
        console.log("Cleaning up scanner...");
        if (html5QrCodeRef.current.isScanning) {
          html5QrCodeRef.current.stop()
            .then(() => {
              console.log("Scanner stopped successfully");
              html5QrCodeRef.current.clear();
            })
            .catch(err => {
              console.error("Error stopping scanner:", err);
            });
        }
      }
    };
  }, [onScanSuccess]);

  const handleClose = () => {
    if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      html5QrCodeRef.current.stop()
        .then(() => {
          console.log("Scanner stopped on close");
          onClose();
        })
        .catch(err => {
          console.error("Error stopping scanner:", err);
          onClose();
        });
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl max-w-2xl w-full p-6 relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClose}
          className="absolute top-4 right-4 text-white hover:bg-slate-800"
        >
          <X className="w-5 h-5" />
        </Button>

        <h2 className="text-2xl font-bold text-white mb-4">
          <Camera className="inline w-6 h-6 mr-2" />
          Scan Barcode
        </h2>
        
        {error ? (
          <div className="mb-4 p-4 bg-red-900/40 border border-red-500/30 rounded-lg">
            <p className="text-red-300 text-sm font-semibold mb-2">Camera Access Error</p>
            <p className="text-red-200 text-xs">{error}</p>
            <p className="text-red-200 text-xs mt-2">
              • Make sure your browser has permission to access the camera<br/>
              • Try refreshing the page<br/>
              • Check if another app is using the camera
            </p>
          </div>
        ) : (
          <div className="mb-4">
            <p className="text-blue-200 text-sm mb-2">
              {isScanning ? 'Point your camera at a barcode...' : 'Initializing camera...'}
            </p>
            <p className="text-blue-300 text-xs">
              Supported formats: UPC, EAN (most product barcodes)
            </p>
          </div>
        )}

        <div 
          id="barcode-scanner-region" 
          className="w-full rounded-lg overflow-hidden"
          style={{ minHeight: '400px', maxHeight: '500px' }}
        />

        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={handleClose}
            className="glass border-red-400 text-red-300 hover:bg-red-500/20"
          >
            Cancel Scan
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
